import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { flushActivity, getRecentActivity, getTrackingContext } from 'src/utilities/activityTracker';
import { submitIssueReport } from 'src/utilities/LanternApiClient';
import useAuth from 'src/hooks/useAuth';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

/**
 * Feedback/Issue widget with floating launcher.
 * Allows users to submit feedback or create Jira issue reports with activity tracking.
 */
const LAUNCHER_POSITION_KEY = 'console:feedback-widget-position';
const DRAG_THRESHOLD_PX = 6;
const VIEWPORT_MARGIN_PX = 16;
const POSITION_LOCKED_ROUTES = ['/monday-explorer'];

const FeedbackWidget = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const launcherRef = useRef(null);
  const dragStateRef = useRef(null);
  const didDragRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [severity, setSeverity] = useState('NEXT_7_DAYS');
  const [summary, setSummary] = useState('');
  const [clientName, setClientName] = useState('');
  const [retailer, setRetailer] = useState('Multi-retailer');
  const [details, setDetails] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [launcherPosition, setLauncherPosition] = useState(null);
  const [recentActivity, setRecentActivity] = useState(getRecentActivity());

  useEffect(() => {
    const handler = () => setRecentActivity(getRecentActivity());
    window.addEventListener('activity:update', handler);
    return () => window.removeEventListener('activity:update', handler);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LAUNCHER_POSITION_KEY);
      if (stored) {
        setLauncherPosition(JSON.parse(stored));
      }
    } catch (_error) {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (!launcherPosition) {
      return;
    }

    try {
      localStorage.setItem(LAUNCHER_POSITION_KEY, JSON.stringify(launcherPosition));
    } catch (_error) {
      // ignore storage errors
    }
  }, [launcherPosition]);

  const sanitizePath = (path = '') => {
    try {
      const url = new URL(path, window.location.origin);
      const redactedParams = [];
      url.searchParams.forEach((_, key) => redactedParams.push(`${key}=***`));
      const search = redactedParams.length ? `?${redactedParams.join('&')}` : '';
      const cleanPath = url.pathname.replace(
        /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
        '****',
      );
      return `${cleanPath}${search}`;
    } catch (e) {
      return path;
    }
  };

  const describeEvent = (event) => {
    if (event.type === 'route') return `[${new Date(event.ts).toLocaleTimeString()}] Navigated to ${event.route}`;
    if (event.type === 'click') {
      const label = event.meta?.label || event.action;
      const value = event.meta?.value ? ` (value: ${event.meta.value})` : '';
      return `[${new Date(event.ts).toLocaleTimeString()}] Clicked "${label}"${value} on ${event.route}`;
    }
    if (event.type === 'api') {
      const { method, path, status, latencyMs } = event.api || {};
      return `[${new Date(event.ts).toLocaleTimeString()}] API ${method} ${sanitizePath(path)} → ${status} (${latencyMs} ms)`;
    }
    return `[${new Date(event.ts || Date.now()).toLocaleTimeString()}] ${event.type}`;
  };

  const resetIssue = () => {
    setSeverity('NEXT_7_DAYS');
    setSummary('');
    setClientName('');
    setRetailer('Multi-retailer');
    setDetails('');
    setAttachments([]);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
      }),
    ],
    content: details,
    onUpdate: ({ editor }) => {
      setDetails(editor.getText());
    },
  });

  useEffect(() => {
    if (editor && details === '') {
      editor.commands.clearContent();
    }
  }, [details, editor]);

  const handleAttachmentAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_FILES = 5;

    // Check file count
    if (attachments.length + files.length > MAX_FILES) {
      enqueueSnackbar(`Maximum ${MAX_FILES} files allowed`, { variant: 'warning' });
      return;
    }

    // Check file sizes
    const oversizedFiles = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      enqueueSnackbar(`Files must be under 10MB: ${oversizedFiles.map((f) => f.name).join(', ')}`, {
        variant: 'warning',
      });
      return;
    }

    setAttachments((prev) => [...prev, ...files]);
  };

  const handleAttachmentRemove = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleIssueSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const userEmail = user?.email || user?.mail || user?.userPrincipalName || 'unknown';
    const tracking = getTrackingContext();
    try {
      let flushedPayload = null;
      try {
        flushedPayload = await flushActivity('issue', true);
      } catch (_error) {
        flushedPayload = null;
      }

      const activityEvents = flushedPayload?.events || recentActivity;
      const activityEventCount = flushedPayload?.eventCount || tracking.activityEventCount;

      const formData = new FormData();
      formData.append('groupId', tracking.groupId);
      formData.append('sessionId', tracking.sessionId);
      formData.append('userEmail', userEmail);
      formData.append('submittedAtUtc', new Date().toISOString());
      formData.append('summary', summary.trim());
      formData.append('details', editor?.getHTML() || details);
      formData.append('clientName', clientName.trim());
      formData.append('retailer', retailer);
      formData.append('consolePageUrl', window.location.origin + window.location.pathname);
      formData.append('severity', severity);
      formData.append('activityEventCount', activityEventCount.toString());

      // Add activity JSON as a file with groupId in filename
      const activityBlob = new Blob([JSON.stringify(activityEvents, null, 2)], { type: 'application/json' });
      formData.append('attachments', activityBlob, `activity-log-${tracking.groupId}.json`);

      // Add user attachments
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await submitIssueReport(formData);
      const ticketNumber =
        response?.body?.ticketNumber ||
        response?.body?.issueKey ||
        response?.body?.key ||
        response?.ticketNumber ||
        response?.issueKey ||
        response?.key;

      if (!response?.success || !ticketNumber) {
        throw new Error('Issue report did not return a ticket number.');
      }

      enqueueSnackbar(`Issue created: ${ticketNumber}`, { variant: 'success' });

      setOpen(false);
      resetIssue();
    } catch (err) {
      console.error('Issue report submit failed', err);
      enqueueSnackbar('Failed to create issue. Please try again.', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const disableIssueSubmit = isSubmitting || !summary.trim() || !details.trim() || !clientName.trim();

  const clampLauncherPosition = (left, top) => {
    const launcherRect = launcherRef.current?.getBoundingClientRect();
    const launcherWidth = launcherRect?.width || 0;
    const launcherHeight = launcherRect?.height || 0;
    const maxLeft = Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - launcherWidth - VIEWPORT_MARGIN_PX);
    const maxTop = Math.max(VIEWPORT_MARGIN_PX, window.innerHeight - launcherHeight - VIEWPORT_MARGIN_PX);

    return {
      left: Math.min(Math.max(left, VIEWPORT_MARGIN_PX), maxLeft),
      top: Math.min(Math.max(top, VIEWPORT_MARGIN_PX), maxTop),
    };
  };

  useEffect(() => {
    if (!launcherPosition) {
      return;
    }

    const syncLauncherIntoViewport = () => {
      const nextPosition = clampLauncherPosition(launcherPosition.left, launcherPosition.top);
      if (nextPosition.left !== launcherPosition.left || nextPosition.top !== launcherPosition.top) {
        setLauncherPosition(nextPosition);
      }
    };

    const frameId = window.requestAnimationFrame(syncLauncherIntoViewport);
    window.addEventListener('resize', syncLauncherIntoViewport);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', syncLauncherIntoViewport);
    };
  }, [launcherPosition]);

  const handleLauncherPointerDown = (event) => {
    if (isPositionLocked) return;
    const launcherRect = launcherRef.current?.getBoundingClientRect();
    if (!launcherRect) {
      return;
    }

    didDragRef.current = false;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: launcherRect.left,
      originTop: launcherRect.top,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleLauncherPointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (!didDragRef.current && Math.hypot(deltaX, deltaY) >= DRAG_THRESHOLD_PX) {
      didDragRef.current = true;
    }

    const nextPosition = clampLauncherPosition(dragState.originLeft + deltaX, dragState.originTop + deltaY);
    setLauncherPosition(nextPosition);
  };

  const handleLauncherPointerUp = (event) => {
    if (dragStateRef.current?.pointerId !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragStateRef.current = null;
  };

  const handleLauncherClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    setOpen(true);
  };

  const isPositionLocked = typeof pathname === 'string' && POSITION_LOCKED_ROUTES.some((r) => pathname.startsWith(r));
  const shouldHideOnCurrentPage = typeof pathname === 'string' && pathname.startsWith('/forecasting-2');
  if (shouldHideOnCurrentPage || dismissed) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          zIndex: 1300,
          left: !isPositionLocked && launcherPosition ? launcherPosition.left : 'auto',
          top: !isPositionLocked && launcherPosition ? launcherPosition.top - 8 : 'auto',
          right: !isPositionLocked && launcherPosition ? 'auto' : 8,
          bottom: !isPositionLocked && launcherPosition ? 'auto' : 8,
          pt: '8px',
          pr: '8px',
          pointerEvents: 'auto',
          '& .feedback-dismiss': { opacity: 0, transition: 'opacity 120ms' },
          '&:hover .feedback-dismiss': { opacity: 1 },
        }}
      >
        <IconButton
          className="feedback-dismiss"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 16,
            height: 16,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.15)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            zIndex: 1,
            '&:hover': { background: '#f5f5f5' },
          }}
        >
          <CloseIcon sx={{ fontSize: 10, color: '#555' }} />
        </IconButton>
        <Box
          ref={launcherRef}
          onClick={handleLauncherClick}
          onPointerDown={handleLauncherPointerDown}
          onPointerMove={handleLauncherPointerMove}
          onPointerUp={handleLauncherPointerUp}
          onPointerCancel={handleLauncherPointerUp}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.1,
            py: 1.1,
            borderRadius: 999,
            boxShadow: '0 12px 30px rgba(0, 68, 133, 0.28)',
            cursor: 'pointer',
            border: '1px solid rgba(12, 73, 124, 0.18)',
            background: 'linear-gradient(135deg, #0a5ea8 0%, #0c7fbd 100%)',
            color: '#ffffff',
            pointerEvents: 'auto',
            userSelect: 'none',
            touchAction: 'none',
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            '&:hover': {
              boxShadow: '0 16px 36px rgba(0, 68, 133, 0.34)',
            },
          }}
        >
          <EditNoteOutlinedIcon sx={{ color: 'inherit', fontSize: 28 }} />
        </Box>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} pr={5}>
            <Box
              sx={{
                flexGrow: 1,
                color: 'text.primary',
              }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.05 }}>
                Report an Issue
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Title"
              required
              fullWidth
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Brief description of the issue"
            />
            <TextField
              label="Client name"
              fullWidth
              required
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Who is impacted?"
            />
            <TextField
              select
              label="Retailer"
              value={retailer}
              onChange={(e) => setRetailer(e.target.value)}
              size="small"
              required
            >
              <MenuItem value="Albertson's">Albertson's</MenuItem>
              <MenuItem value="Amazon">Amazon</MenuItem>
              <MenuItem value="Costco">Costco</MenuItem>
              <MenuItem value="Harvest Group Internal">Harvest Group Internal</MenuItem>
              <MenuItem value="Instacart">Instacart</MenuItem>
              <MenuItem value="Kroger">Kroger</MenuItem>
              <MenuItem value="Multi-retailer">Multi-retailer</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Sam's Club">Sam's Club</MenuItem>
              <MenuItem value="Target">Target</MenuItem>
              <MenuItem value="Walmart">Walmart</MenuItem>
            </TextField>
            <TextField
              select
              label="Ideal resolution"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              size="small"
              required
            >
              <MenuItem value="TODAY">Today</MenuItem>
              <MenuItem value="TOMORROW">Tomorrow</MenuItem>
              <MenuItem value="NEXT_7_DAYS">Next 7 days</MenuItem>
              <MenuItem value="NEXT_30_DAYS">Next 30 days</MenuItem>
              <MenuItem value="NEXT_90_DAYS">Next 90 days</MenuItem>
            </TextField>
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={500}>
                Details<span style={{ color: '#d32f2f' }}> *</span>
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    disabled={!editor}
                    aria-label="Bold"
                    sx={{ bgcolor: editor?.isActive('bold') ? 'action.selected' : 'transparent' }}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    disabled={!editor}
                    aria-label="Italic"
                    sx={{ bgcolor: editor?.isActive('italic') ? 'action.selected' : 'transparent' }}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    disabled={!editor}
                    aria-label="Bulleted list"
                    sx={{ bgcolor: editor?.isActive('bulletList') ? 'action.selected' : 'transparent' }}
                  >
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    disabled={!editor}
                    aria-label="Numbered list"
                    sx={{ bgcolor: editor?.isActive('orderedList') ? 'action.selected' : 'transparent' }}
                  >
                    <FormatListNumberedIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    minHeight: 120,
                    maxHeight: 280,
                    p: 1.5,
                    overflowY: 'auto',
                    '& .ProseMirror': {
                      outline: 'none',
                      minHeight: 120,
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& ul, & ol': {
                        paddingLeft: '20px',
                        margin: '8px 0',
                      },
                      '& ul': {
                        listStyleType: 'disc',
                      },
                      '& ol': {
                        listStyleType: 'decimal',
                      },
                      '& ul > li': {
                        display: 'list-item',
                        listStyleType: 'disc',
                      },
                      '& ol > li': {
                        display: 'list-item',
                        listStyleType: 'decimal',
                      },
                      '& li': {
                        marginBottom: '4px',
                      },
                      '&:empty:before': {
                        content: '"What happened?"',
                        color: 'text.disabled',
                        pointerEvents: 'none',
                      },
                    },
                  }}
                >
                  <EditorContent editor={editor} />
                </Box>
              </Box>
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={500}>
                Attachments
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Add files
                  <input type="file" multiple hidden onChange={handleAttachmentAdd} />
                </Button>
                {attachments.length > 0 && (
                  <List dense sx={{ mt: 1 }}>
                    {attachments.map((file, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton edge="end" size="small" onClick={() => handleAttachmentRemove(index)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        }
                        sx={{ px: 1, py: 0.5 }}
                      >
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(1)} KB`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon color="action" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                We&apos;ll create a Jira ticket with your summary, details, and recent activity timeline.
              </Typography>
            </Stack>
            <Accordion sx={{ mt: 1 }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  Preview recent activity
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 1 }}>
                  {recentActivity.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No recent activity captured yet.
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {recentActivity.map((evt, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemText
                            primaryTypographyProps={{
                              variant: 'caption',
                              color: 'text.secondary',
                              sx: { wordBreak: 'break-word', whiteSpace: 'normal' },
                            }}
                            primary={describeEvent(evt)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Button onClick={resetIssue} disabled={isSubmitting}>
              Clear draft
            </Button>
          </Box>
          <Button onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Tooltip title={disableIssueSubmit ? 'Complete required fields' : ''}>
            <span>
              <Button variant="contained" disabled={disableIssueSubmit} onClick={handleIssueSubmit}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
